import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button as SubmitButton } from "@/components/ui/Button";
import { IconSymbol } from "@/components/ui/IconSymbol";
import { COUNTRIES } from "@/constants/countries";
import { useAuth } from "@/context/AuthContext";
import { useColorScheme } from "@/hooks/useColorScheme";
import { useThemeColor } from "@/hooks/useThemeColor";
import { UserDocument } from "@/types/user";
import { isUsernameAvailable as checkUsernameAvailability } from "@/utils/username.native";
import { Feather } from "@expo/vector-icons";
import DateTimePicker from "@react-native-community/datetimepicker";
import auth from "@react-native-firebase/auth";
import firestore, {
  FirebaseFirestoreTypes,
  Timestamp,
} from "@react-native-firebase/firestore";
import storage from "@react-native-firebase/storage";
import { Picker } from "@react-native-picker/picker";
import { Image } from "expo-image";
import * as ImagePicker from "expo-image-picker";
import { StatusBar } from "expo-status-bar";

import { t } from "i18next";
import { VerifiedDisplayName } from "@/components/ui/VerifiedDisplayName";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  ActivityIndicator,
  Alert,
  Button,
  Platform,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from "react-native";

const DefaultProfileImage = require("../../assets/images/default-profile.jpg");

const formatDateOfBirth = (
  dob?: FirebaseFirestoreTypes.Timestamp | Date | string | null,
): string => {
  if (!dob) return "N/A";
  let date: Date | null = null;
  if (dob instanceof Date) {
    date = dob;
  } else if (typeof (dob as any)?.toDate === "function") {
    date = (dob as FirebaseFirestoreTypes.Timestamp).toDate();
  } else if (typeof dob === "string") {
    const parsed = new Date(dob);
    if (!isNaN(parsed.getTime())) {
      date = parsed;
    }
  }
  if (!date) return "N/A";
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = date.toLocaleString("en-GB", { month: "short" }).toLowerCase();
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

export default function ProfileScreen() {
  const isProfileChanged = () => {
    if (!userData) return false;
    const isChanged =
      username !== (userData.username || "") ||
      country !== (userData.country || "") ||
      bio !== (userData.bio || "");
    return isChanged;
  };
  const { user } = useAuth();
  const [userData, setUserData] = useState<UserDocument | null>(null);
  const [loading, setLoading] = useState(true);

  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [usernameAvailabilityError, setUsernameAvailabilityError] =
    useState("");
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [country, setCountry] = useState("");
  const [bio, setBio] = useState("");
  const [fullNameSet, setFullNameSet] = useState(false);
  const [uploading, setUploading] = useState(false);
  const primaryColor = useThemeColor({}, "primary");
  const tintColor = useThemeColor({}, "tint");
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const dateOfBirthSaved = !!userData?.dateOfBirth;
  const colorScheme = useColorScheme();

  const thirteenYearsAgo = new Date(
    new Date().getFullYear() - 13,
    new Date().getMonth(),
    new Date().getDate(),
  );

  const [showDatePicker, setShowDatePicker] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
    },
  });

  const firstName = watch("firstName");
  const lastName = watch("lastName");

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          const userDocRef = firestore().collection("users").doc(user.uid);
          const userDocSnap = await userDocRef.get();
          if (userDocSnap.exists()) {
            const data = userDocSnap.data() as UserDocument;
            setUserData(data);
            setUsername(data.username || "");
            setDateOfBirth(
              data.dateOfBirth
                ? data.dateOfBirth instanceof Timestamp
                  ? data.dateOfBirth.toDate()
                  : typeof data.dateOfBirth === "string"
                    ? new Date(data.dateOfBirth)
                    : null
                : null,
            );
            setCountry(data.country || "");
            setBio(data.bio || "");
            setFullNameSet(data.fullNameSet || false);
            setValue("firstName", data.firstName || "");
            setValue("lastName", data.lastName || "");
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, setValue]);

  const handleProfilePicChange = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: "images",
      allowsEditing: true,
      aspect: [1, 1],
      quality: 1,
    });

    if (!result.canceled) {
      setUploading(true);
      try {
        const fileUri = result.assets[0].uri;
        const response = await fetch(fileUri);
        const blob = await response.blob();
        const refPath = `profile-pictures/${user?.uid}/original.jpeg`;
        const ref = storage().ref(refPath);
        await ref.put(blob);
        const url = await ref.getDownloadURL();
        const userDocRef = firestore().collection("users").doc(user?.uid!);
        await userDocRef.update({ photoURL: url });
        // Keep Firebase Auth in sync so denormalized writes (e.g. comment
        // author.photoURL = user.photoURL) pick up the new URL.
        await auth().currentUser?.updateProfile({ photoURL: url });
        setUserData((prev) => (prev ? { ...prev, photoURL: url } : null));
      } catch (error) {
        console.error("Error uploading image: ", error);
        Alert.alert(t("ERROR"), t("FAILED_TO_UPLOAD_IMAGE"));
      } finally {
        setUploading(false);
      }
    }
  };

  const handleFullNameUpdate = async (data: {
    firstName: string;
    lastName: string;
  }) => {
    if (user) {
      Alert.alert(
        t("CONFIRM_FULL_NAME_AND_DATE_OF_BIRTH"),
        t("CONFIRM_FULL_NAME_MESSAGE"),
        [
          {
            text: t("CANCEL"),
            style: "cancel",
          },
          {
            text: "OK",
            onPress: async () => {
              try {
                const userDocRef = firestore()
                  .collection("users")
                  .doc(user.uid);
                await userDocRef.update({
                  firstName: data.firstName,
                  lastName: data.lastName,
                  displayName: `${data.firstName} ${data.lastName}`,
                  fullNameSet: true,
                });

                setUserData((prev) =>
                  prev
                    ? {
                        ...prev,
                        firstName: data.firstName,
                        lastName: data.lastName,
                        displayName: `${data.firstName} ${data.lastName}`,
                        fullNameSet: true,
                      }
                    : prev,
                );

                setFullNameSet(true);
                Alert.alert(t("FULL_NAME_SET"), t("FULL_NAME_MESSAGE_SET"));
              } catch (error) {
                console.error("Error updating full name:", error);
                Alert.alert(t("ERROR"), t("FAILED_TO_UPDATE_PROFILE"));
              }
            },
          },
        ],
      );
    }
  };

  const handleDateOfBirthUpdate = async () => {
    if (!user) return;
    if (!dateOfBirth) {
      Alert.alert(t("ERROR"), t("PLEASE_SELECT_DATE_OF_BIRTH"));
      return;
    }
    Alert.alert(t("CONFIRM_DATE_OF_BIRTH"), t("CONFIRM_DATE_OF_BIRTH_MESSAGE"), [
      { text: t("CANCEL"), style: "cancel" },
      {
        text: "OK",
        onPress: async () => {
          try {
            const jsDate =
              dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth);
            const userDocRef = firestore().collection("users").doc(user.uid);
            const dobTimestamp = Timestamp.fromDate(jsDate);
            await userDocRef.update({ dateOfBirth: dobTimestamp });

            setUserData((prev) =>
              prev ? { ...prev, dateOfBirth: dobTimestamp } : prev,
            );

            Alert.alert(
              t("DATE_OF_BIRTH_SET"),
              t("DATE_OF_BIRTH_MESSAGE_SET"),
            );
          } catch (error) {
            console.error("Error updating date of birth:", error);
            Alert.alert(t("ERROR"), t("FAILED_TO_UPDATE_PROFILE"));
          }
        },
      },
    ]);
  };

  const validateUsername = (text: string) => {
    if (/\s/.test(text)) {
      setUsernameError(t("USERNAME_CANNOT_CONTAIN_SPACES"));
    } else {
      setUsernameError("");
    }
    setUsername(text);
  };

  const isUsernameAvailable = async (candidateUsername: string) => {
    if (!user) return false;

    return checkUsernameAvailability(firestore(), candidateUsername, user.uid);
  };

  useEffect(() => {
    if (!user || !userData) return;

    const normalizedUsername = username.trim();
    const currentUsername = (userData.username || "").trim();

    if (
      !normalizedUsername ||
      !!usernameError ||
      normalizedUsername === currentUsername
    ) {
      setUsernameAvailabilityError("");
      setIsCheckingUsername(false);
      return;
    }

    let cancelled = false;
    setIsCheckingUsername(true);

    const timeoutId = setTimeout(async () => {
      try {
        const available = await isUsernameAvailable(normalizedUsername);
        if (cancelled) return;

        setUsernameAvailabilityError(
          available ? "" : t("USERNAME_ALREADY_TAKEN"),
        );
      } catch (error) {
        console.error("Error checking username availability:", error);
        if (!cancelled) {
          setUsernameAvailabilityError("");
        }
      } finally {
        if (!cancelled) {
          setIsCheckingUsername(false);
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [user, userData, username, usernameError]);

  const handleUpdate = async () => {
    if (user) {
      if (usernameError) {
        Alert.alert(t("ERROR"), usernameError);
        return;
      }

      const normalizedUsername = username.trim();
      const currentUsername = (userData?.username || "").trim();

      if (normalizedUsername !== username) {
        setUsername(normalizedUsername);
      }

      if (normalizedUsername !== currentUsername) {
        try {
          setIsCheckingUsername(true);
          const available = await isUsernameAvailable(normalizedUsername);
          if (!available) {
            const unavailableMessage = t("USERNAME_ALREADY_TAKEN");
            setUsernameAvailabilityError(unavailableMessage);
            Alert.alert(t("ERROR"), unavailableMessage);
            return;
          }
        } catch (error) {
          console.error("Error checking username availability:", error);
        } finally {
          setIsCheckingUsername(false);
        }
      }

      try {
        const userDocRef = firestore().collection("users").doc(user.uid);

        await userDocRef.update({
          username: normalizedUsername,
          country,
          bio,
        });

        setUserData((prev) =>
          prev
            ? {
                ...prev,
                username: normalizedUsername,
                country,
                bio,
              }
            : prev,
        );
        Alert.alert(
          t("PROFILE_UPDATED"),
          t("YOUR_PROFILE_HAS_BEEN_UPDATED_SUCCESSFULLY"),
        );
      } catch (error) {
        console.error("Error updating user data:", error);
        Alert.alert(t("ERROR"), t("FAILED_TO_UPDATE_PROFILE"));
      }
    }
  };

  const onDateChange = (_event: any, selectedDate?: Date) => {
    const currentDate = selectedDate || dateOfBirth;
    setShowDatePicker(Platform.OS === "ios");
    setDateOfBirth(currentDate);
  };

  return (
    <ThemedView style={{ flex: 1 }}>
      <StatusBar style="dark" animated={true} />
      <ScrollView>
        <ThemedView style={styles.container}>
          <ThemedView style={styles.header}>
            <ThemedView style={styles.profilePicContainer}>
              <TouchableOpacity
                onPress={handleProfilePicChange}
                disabled={uploading || loading}
              >
                <Image
                  source={userData?.photoURL}
                  style={styles.profilePic}
                  placeholder={DefaultProfileImage}
                  cachePolicy="disk"
                  transition={300}
                />
                <ThemedView
                  style={[styles.cameraBadge, { backgroundColor: primaryColor }]}
                >
                  <Feather name="camera" size={18} color="#fff" />
                </ThemedView>
                {uploading && (
                  <ThemedView style={styles.uploadingOverlay}>
                    <ActivityIndicator size="large" color="#fff" />
                  </ThemedView>
                )}
              </TouchableOpacity>
            </ThemedView>
            <VerifiedDisplayName
              user={userData}
              style={styles.name}
              loading={loading}
              badgeSize={22}
              containerStyle={{ justifyContent: "center" }}
            />
          </ThemedView>
          {!fullNameSet && (
            <ThemedView style={[styles.details, styles.fullNameForm]}>
              <ThemedText style={styles.label}>{t("FIRST_NAME")}:</ThemedText>
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.input}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    value={value}
                    editable={!fullNameSet}
                  />
                )}
                name="firstName"
              />
              {errors.firstName && (
                <ThemedText style={styles.errorText}>
                  {t("PLEASE_ENTER_FIRST_NAME")}
                </ThemedText>
              )}
              <ThemedText style={styles.label}>{t("LAST_NAME")}:</ThemedText>
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.input}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    value={value}
                    editable={!fullNameSet}
                  />
                )}
                name="lastName"
              />
              {errors.lastName && (
                <ThemedText style={styles.errorText}>
                  {t("PLEASE_ENTER_LAST_NAME")}
                </ThemedText>
              )}

              <ThemedView style={styles.updateProfileButton}>
                <Button
                  title={t("SAVE_PERSONAL_DATA")}
                  onPress={handleSubmit(handleFullNameUpdate)}
                  disabled={fullNameSet || !firstName || !lastName}
                />
              </ThemedView>
            </ThemedView>
          )}

          {!dateOfBirthSaved && (
            <ThemedView style={[styles.details, styles.fullNameForm]}>
              <ThemedText style={styles.label}>
                {t("DATE_OF_BIRTH")}:
              </ThemedText>
              <ThemedView style={styles.dateContainer}>
                <TouchableOpacity
                  style={[styles.dateInputLeft, styles.input]}
                  onPress={() => setShowDatePicker(true)}
                >
                  <IconSymbol name="calendar" size={20} color={tintColor} />
                  <ThemedText style={styles.buttonText}>
                    {dateOfBirth
                      ? formatDateOfBirth(Timestamp.fromDate(dateOfBirth))
                      : ""}
                  </ThemedText>
                </TouchableOpacity>
              </ThemedView>

              {showDatePicker && (
                <DateTimePicker
                  testID="datePicker"
                  mode="date"
                  value={dateOfBirth || thirteenYearsAgo}
                  is24Hour={true}
                  display="default"
                  onChange={onDateChange}
                  maximumDate={thirteenYearsAgo}
                />
              )}

              <ThemedView style={styles.updateProfileButton}>
                <Button
                  title={t("SAVE_DATE_OF_BIRTH")}
                  onPress={handleDateOfBirthUpdate}
                  disabled={!dateOfBirth}
                />
              </ThemedView>
            </ThemedView>
          )}
          {dateOfBirthSaved && (
            <ThemedView style={styles.details}>
              <ThemedText style={styles.label}>
                {`${t("DATE_OF_BIRTH")}: ${formatDateOfBirth(dateOfBirth)}`}
              </ThemedText>
            </ThemedView>
          )}
          <ThemedView style={styles.details}>
            <ThemedText style={styles.label}>{t("USERNAME")}:</ThemedText>
            <TextInput
              style={styles.input}
              value={username}
              maxLength={20}
              onChangeText={validateUsername}
            />
            {usernameError ? (
              <ThemedText style={styles.errorText}>{usernameError}</ThemedText>
            ) : usernameAvailabilityError ? (
              <ThemedText style={styles.errorText}>
                {usernameAvailabilityError}
              </ThemedText>
            ) : isCheckingUsername ? (
              <ThemedText style={styles.usernameStatusText}>
                {t("CHECKING_USERNAME_AVAILABILITY")}
              </ThemedText>
            ) : null}
            <ThemedText style={styles.label}>{t("COUNTRY")}:</ThemedText>
            <ThemedView style={styles.pickerContainer}>
              <Picker
                selectedValue={country}
                style={styles.picker}
                onValueChange={(itemValue) => setCountry(itemValue)}
              >
                {COUNTRIES.map((c) => (
                  <Picker.Item
                    key={c.value}
                    label={`${c.flag} ${c.label}`}
                    value={c.value}
                  />
                ))}
              </Picker>
            </ThemedView>
            <ThemedText style={styles.label}>{t("BIO")}:</ThemedText>
            <TextInput
              style={[styles.input, styles.bioInput]}
              value={bio}
              onChangeText={setBio}
              multiline
            />
            <ThemedView style={styles.updateProfileButton}>
              <SubmitButton
                text={t("UPDATE_PROFILE")}
                onPress={handleUpdate}
                style={{ width: "100%" }}
                disabled={
                  !isProfileChanged() ||
                  !!usernameError ||
                  !!usernameAvailabilityError ||
                  isCheckingUsername
                }
              />
            </ThemedView>
          </ThemedView>
        </ThemedView>
      </ScrollView>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  safeAreaContainer: {
    flex: 1,
  },
  activityIndicatorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  container: {
    flex: 1,
    padding: 20,
  },
  header: {
    marginTop: 20,
    alignItems: "center",
    marginBottom: 20,
  },
  profilePicContainer: {
    position: "relative",
    marginBottom: 10,
  },
  profilePic: {
    width: 150,
    height: 150,
    borderRadius: 75,
  },
  cameraBadge: {
    position: "absolute",
    bottom: 4,
    right: 4,
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: "#fff",
    justifyContent: "center",
    alignItems: "center",
  },
  uploadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    borderRadius: 75,
  },
  name: {
    fontSize: 24,
    fontWeight: "bold",
  },
  rank: {
    fontSize: 16,
    color: "gray",
  },
  stats: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginBottom: 20,
  },
  details: {
    width: "100%",
    marginBottom: 20,
  },
  fullNameForm: {
    maxWidth: 500,
    width: "100%",
    alignSelf: "center",
  },
  label: {
    fontSize: 16,
    marginTop: 10,
  },
  input: {
    fontSize: 16,

    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    padding: 10,
    marginTop: 5,
    backgroundColor: "white",
    color: "#000",
  },
  bioInput: {
    height: 100,
    textAlignVertical: "top",
  },
  buttonText: {
    marginLeft: 8,
    fontSize: 16,
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    marginTop: 5,
    backgroundColor: "white",
    justifyContent: "center",
    paddingHorizontal: 10,
  },
  picker: {
    width: "100%",
    backgroundColor: "transparent",
    color: "#000",
    marginHorizontal: -10,
  },
  dateContainer: {
    alignItems: "center",
    flexDirection: "row",
    borderRadius: 8,
    alignSelf: "flex-start",
  },
  dateInputLeft: {
    flexDirection: "row",
  },
  updateProfileButton: {
    marginTop: 10,
    backgroundColor: "#007AFF",
    borderRadius: 5,
  },
  errorText: {
    color: "red",
    fontSize: 14,
  },
  usernameStatusText: {
    fontSize: 14,
    marginTop: 4,
  },
});
